import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog.tsx";
import {
  catalogEntry,
  formatDate,
  formatPricePerInterval,
  intervalAdjective,
  intervalAdverb,
  planPhrase,
} from "@/components/billing/planCopy.ts";
import type { PlanChangeTiming, PlanResp } from "@/types/billing.ts";
import {
  PLAN_TIER_LABELS as TIER_LABELS,
  type BillingInterval,
  type PaidTier,
  type PlanTier,
} from "@/types/entitlements.ts";

/**
 * A change the user asked for, with the timing the server will apply
 * (`planChangeTiming`, shared with the API) so the dialog promises exactly
 * what will happen.
 */
export type PlanChangeIntent =
  | {
      kind: "change";
      tier: PaidTier;
      interval: BillingInterval;
      timing: Exclude<PlanChangeTiming, "noop">;
    }
  /** `toFree` when asked from the Free row: the same cancellation, named for where it lands. */
  | { kind: "cancel"; toFree?: boolean };

/**
 * The date the paid period ends, which is when a scheduled change lands.
 * Without Stripe's own period end the copy says so in words rather than
 * guessing: the plan's `periodEnd` is the end of the usage allowance, which on
 * a weekly allowance is next Monday, not the billing date.
 */
function paidPeriodEnd(plan: PlanResp): string {
  const end = plan.subscription?.currentPeriodEnd;
  return end ? formatDate(end) : "the end of your billing period";
}

/**
 * The tier the subscription sells, which is what a change moves away from. It
 * can differ from the resolved `plan.tier` — a lapsed grace period resolves to
 * Free while the subscription still sells Pro — and the plan options compute
 * their timing from the subscription, so the copy must too.
 */
function subscribedTier(plan: PlanResp): PlanTier {
  return plan.subscription?.tier ?? plan.tier;
}

/**
 * Confirmation for every plan change, stating what happens and when *before*
 * the user commits.
 *
 * Changes that give less stop nothing at the moment they are requested: data,
 * connectors, and routines keep running until the paid period ends, and only
 * then does the smaller allowance apply. Saying so up front is the decided
 * posture — the alternative is a user who downgrades expecting to lose
 * something now, or who loses something later without having been told.
 */
export const PlanChangeDialog = ({
  intent,
  plan,
  onConfirm,
  onOpenChange,
  pending,
}: {
  intent: PlanChangeIntent | null;
  plan: PlanResp;
  onConfirm: (intent: PlanChangeIntent) => void;
  onOpenChange: (open: boolean) => void;
  pending: boolean;
}) => {
  const copy = intent ? describe(intent, plan) : null;

  return (
    <AlertDialog open={intent !== null} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{copy?.title}</AlertDialogTitle>
          <AlertDialogDescription>{copy?.body}</AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Keep current plan</AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            onClick={() => intent && onConfirm(intent)}
            data-statsig-name={`billing-plan-${intent?.kind === "change" ? intent.timing : "cancel"}-confirm`}
          >
            {copy?.action}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

type Copy = { title: string; body: string; action: string };

function describe(intent: PlanChangeIntent, plan: PlanResp): Copy {
  if (intent.kind === "cancel") {
    return {
      title: intent.toFree ? "Switch to Free?" : "Cancel your plan?",
      body: `Your data, connectors, and routines stay intact. You keep ${TIER_LABELS[subscribedTier(plan)]} until ${paidPeriodEnd(plan)} — the period you've already paid for — and from then you'll be on ${planPhrase(plan, "free")}. Actions beyond that allowance will be unavailable. You can resume any time before then.${replacesScheduled(plan)}`,
      action: intent.toFree ? "Switch to Free" : "Cancel plan",
    };
  }
  return intent.timing === "immediate" ? describeNow(intent, plan) : describeLater(intent, plan);
}

/** Copy for a change applied right away: a bigger tier, or a move to annual billing. */
function describeNow(intent: Extract<PlanChangeIntent, { kind: "change" }>, plan: PlanResp): Copy {
  const intervalOnly = intent.tier === subscribedTier(plan);
  // The same fallback the server and the plan options use, so a subscription
  // with no mirrored interval gets the copy for the change the server makes.
  const currentInterval = plan.subscription?.interval ?? "month";
  const price = catalogEntry(plan, intent.tier)?.prices[intent.interval];
  const priced = price ? formatPricePerInterval(price, intent.interval) : null;

  const move = intervalOnly
    ? `You'll switch to ${intervalAdjective(intent.interval)} billing right away.`
    : `You'll move to ${planPhrase(plan, intent.tier)} right away, and this ${plan.allowancePeriod}'s usage so far counts toward its bigger allowance.`;

  let charge: string;
  if (currentInterval === intent.interval) {
    // Within one interval the server asks Stripe to invoice the prorated
    // difference on the spot, which keeps the renewal date.
    const renewal = plan.subscription?.currentPeriodEnd;
    const on = renewal ? ` on ${formatDate(renewal)}` : "";
    charge = `We'll charge the difference for the rest of this billing period now. Your plan renews at the new rate${on}.`;
  } else if (intent.interval === "year") {
    charge = `Annual billing starts today: we'll charge ${priced ?? "the annual price"} now, less credit for the unused part of your current plan.`;
  } else {
    // On an interval change the server has Stripe reset the billing anchor,
    // which invoices immediately, and the unused annual time offsets that
    // invoice before any remainder carries forward.
    charge = `Monthly billing starts today: we'll charge ${priced ?? "the monthly price"} now, less credit for the unused part of your annual plan. Any credit left over goes toward future bills.`;
  }

  const action = intervalOnly
    ? `Switch to ${intervalAdjective(intent.interval)} billing`
    : `Upgrade to ${TIER_LABELS[intent.tier]}`;
  return { title: `${action}?`, body: `${move} ${charge}${replacesScheduled(plan)}`, action };
}

/** Copy for a change held to the end of the paid period: a smaller tier or monthly billing. */
function describeLater(
  intent: Extract<PlanChangeIntent, { kind: "change" }>,
  plan: PlanResp,
): Copy {
  const current = TIER_LABELS[subscribedTier(plan)];
  if (intent.tier === subscribedTier(plan)) {
    const action = `Switch to ${intervalAdjective(intent.interval)} billing`;
    return {
      title: `${action}?`,
      body: `Nothing changes until ${paidPeriodEnd(plan)} — the end of the period you've already paid for. From then ${current} is billed ${intervalAdverb(intent.interval)}.${replacesScheduled(plan)}`,
      action,
    };
  }
  const action = `Switch to ${TIER_LABELS[intent.tier]}`;
  return {
    title: `${action}?`,
    body: `Your data, connectors, and routines stay intact. You keep ${current} until ${paidPeriodEnd(plan)} — the period you've already paid for — and from then you'll be on ${planPhrase(plan, intent.tier)}, billed ${intervalAdverb(intent.interval)}. Actions beyond that allowance will be unavailable.${replacesScheduled(plan)}`,
    action,
  };
}

/** Any new request replaces a change already scheduled; say which one goes. */
function replacesScheduled(plan: PlanResp): string {
  const scheduled = plan.subscription?.scheduledChange;
  if (!scheduled) return "";
  const target =
    scheduled.tier === subscribedTier(plan)
      ? `${intervalAdjective(scheduled.interval)} billing`
      : TIER_LABELS[scheduled.tier];
  return ` This replaces your scheduled switch to ${target} on ${formatDate(scheduled.effectiveAt)}.`;
}
